import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons/faCircle';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Radio, useMediaQuery, Theme } from '@material-ui/core';
import { Option } from '@spike/model';
import clsx from 'clsx';
import { ActionsPopup } from 'components/UI/ActionsPopup/ActionsPopup';
import Checkbox from 'components/UI/Checkbox';
import { useTabsStyles } from 'components/UI/V2/Tabs/Tabs.styles';
import Spinner from 'components/UI/V3/Spinner';
import React from 'react';
import { createPortal } from 'react-dom';
import useHideHubSpotChat from 'hooks/useHideHubSpotChat';

export type TabOption = Omit<Option<string>, 'description'> & {
    count?: number;
    mark?: 'error' | 'warning';
    selectedColor?: string;
};

interface TabsProps {
    value: string;
    tabs: Array<TabOption>;
    loading?: boolean;
    onChange: (value: TabOption['id']) => void;
}

export const Tabs = (props: TabsProps) => {
    const classes = useTabsStyles();
    const isLg = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));

    const [isOpen, setIsOpen] = React.useState(false);

    // Hide HubSpot chat when mobile popup is open
    useHideHubSpotChat(isOpen && !isLg);

    const toggleHandler = (tab: TabOption) => {
        const isDesktop = window.matchMedia('(min-width: 960px)').matches;

        if (isDesktop) {
            props.onChange(tab.id);
            return;
        }

        setIsOpen(!isOpen);
    };

    const changeHandler = (tab: TabOption) => {
        props.onChange(tab.id);
        setIsOpen(false);
    };

    return (
        <>
            <div className={classes.root}>
                {props.tabs.map(tab => (
                    <div
                        key={tab.id}
                        className={clsx(classes.tab, {
                            [classes.selected]: props.value === tab.id
                        })}
                        style={
                            tab.selectedColor && props.value === tab.id
                                ? { backgroundColor: tab.selectedColor }
                                : undefined
                        }
                        onClick={() => toggleHandler(tab)}
                    >
                        {tab.name}
                        {tab.mark === 'warning' && (
                            <FontAwesomeIcon
                                icon={faCircle}
                                className={classes.warning}
                            />
                        )}
                        {tab.count !== undefined && (
                            <div className={classes.countContainer}>
                                <span className={clsx({ [classes.hidden]: props.loading })}>({tab.count})</span>
                                {props.loading && (
                                    <div className={classes.spinnerContainer}>
                                        <Spinner size={15} />
                                    </div>
                                )}
                            </div>
                        )}
                        {props.value === tab.id && (
                            <FontAwesomeIcon
                                icon={faAngleDown}
                                className={classes.icon}
                            />
                        )}
                    </div>
                ))}
            </div>

            {isOpen &&
                createPortal(
                    <ActionsPopup
                        actions={props.tabs.map(tab => ({
                            label: tab.name,
                            leftElement: (
                                <Radio
                                    checked={props.value === tab.id}
                                    className={classes.radio}
                                />
                            ),
                            onClick: () => changeHandler(tab)
                        }))}
                        onClose={() => setIsOpen(false)}
                    />,
                    document.body
                )}
        </>
    );
};
